import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import styles from './sportModulesManager.module.css';
import defaultStyles from '../default.module.css';
import SportmodulCard from './SportModuleCard';
import SportmodulForm from './SportModuleForm';
import { Icons } from '../../../../code/IconHandler';
import { usePermissionGuard } from '../../adminPanelTools';
import { showErrorToast } from '../../../../code/ToastHelper';

export default function SportModulManager() {
    const checking = usePermissionGuard('handle_sportmodules');
    const [modules, setModules] = useState([]);        // renamed to plural 'modules'
    const [allPersons, setAllPersons] = useState([]);  // add allPersons state
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');

    const token = localStorage.getItem('token');

    // Fetch all persons once
    const fetchAllPersons = async () => {
        try {
            const res = await axios.get('/api/contact', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAllPersons(res.data);
        } catch (err) {
            console.error('Fehler beim Laden der Personen:', err);
        }
    };

    // Fetch all modules
    const fetchModules = async () => {
        setLoading(true);
        try {
            const res = await axios.get('/api/sportmodule/full/all', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setModules(res.data);

        } catch (err) {
            console.error('Fehler beim Laden der Module:', err);
        } finally {
            setLoading(false);
        }
    };

    // Fetch both modules and persons on mount
    useEffect(() => {
        fetchAllPersons();
        fetchModules();
    }, []);
    // Extract all unique categories from the nested `modules` inside each module
    const allCategories = useMemo(() => {
        return Array.from(
            new Set(
                modules
                    .flatMap(m => (m.modules || []).map(mod => mod.category))
                    .filter(Boolean)
            )
        );
    }, [modules]);
    const enrichedModules = modules.map(modul => {
        const enrichedAssignments = (modul.assignments || []).map(a => ({
            ...a,
            contact: allPersons.find(p => p.id === a.contact_id) || null,
        }));
        return {
            ...modul,
            assignments: enrichedAssignments,
        };
    });

    const handleDelete = async (id) => {
        if (!window.confirm('Wirklich löschen?')) return;
        try {
            await axios.delete(`/api/sportmodule/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            fetchModules();
        } catch (err) {
            showErrorToast("Fehler beim Löschen des Modules.")
        }
    };

    const handleSave = () => {
        setShowModal(false);
        fetchModules();
    };

    if (checking) {
        return (
            <div className={defaultStyles.container}>
                <p>Loading...</p>
            </div>
        );
    }

    return (
        <div className={defaultStyles.container}>
            <h1>Sportmodule verwalten</h1>
            {showModal && (
                <SportmodulForm
                    item={editingItem}
                    onClose={() => setShowModal(false)}
                    onSave={handleSave}
                />
            )}
            <div className={defaultStyles.titleButton}>
                <h2>Module</h2>
                <button className={defaultStyles.blueButton} onClick={() => {
                    setEditingItem(null);
                    setShowModal(true);
                }}>
                    <Icons.Add /> Neues Modul
                </button>
            </div>
            {loading ? (
                <p>Lade...</p>
            ) : (
                <>
                    <div className={styles.filterBar}>
                        <input
                            type="text"
                            placeholder="Nach Modulnamen suchen..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className={defaultStyles.input}
                        />
                        <select
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                            className={defaultStyles.select}
                        >
                            <option value="">Alle Kategorien</option>
                            {allCategories.map((cat, i) => (
                                <option key={i} value={cat}>{cat}</option>
                            ))}
                        </select>
                    </div>
                    <div className={styles.listContainer}>
                        {enrichedModules
                            .filter(m => {
                                const nameMatch = searchTerm
                                    ? (m.modules || []).some(mod =>
                                        mod.title?.toLowerCase().includes(searchTerm.toLowerCase())
                                    )
                                    : true;
                                const categoryMatch = selectedCategory
                                    ? (m.modules || []).some(mod => mod.category === selectedCategory)
                                    : true;
                                return nameMatch && categoryMatch;
                            })
                            .sort((a, b) => {
                                // Sort by first module title alphabetically (assuming at least one module exists)
                                const titleA = (a.modules?.[0]?.title || "").toLowerCase();
                                const titleB = (b.modules?.[0]?.title || "").toLowerCase();
                                return titleA.localeCompare(titleB);
                            })
                            .map(m => (
                                <SportmodulCard
                                    key={m.id}
                                    modul={m}
                                    onEdit={(modul) => {
                                        setEditingItem(modul);
                                        setShowModal(true);
                                    }}
                                    onDelete={handleDelete}
                                />
                            ))}
                    </div>
                </>

            )}
        </div>
    );
}
